import React from 'react';
import { Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTriviaRushDemo } from '../hooks/useTriviaRushDemo';
import { useLayoutVariant } from '../hooks/useLayoutVariant';
import { RushStartScreen } from '../components/trivia-rush/RushStartScreen';
import { QuestionCard } from '../components/trivia-rush/QuestionCard';
import { RushTimer } from '../components/trivia-rush/RushTimer';
import { RushGameOver } from '../components/trivia-rush/RushGameOver';
import { RushTopBar } from '../components/trivia-rush/RushTopBar';
import { RushWavyGrid } from '../components/trivia-rush/RushWavyGrid';

export const TriviaRushDemoPage: React.FC = () => {
  // Dev-only guard
  if (process.env.NODE_ENV !== 'development') {
    return <Navigate to="/trivia-rush" replace />;
  }

  return <TriviaRushDemoContent />;
};

const TriviaRushDemoContent: React.FC = () => {
  const layout = useLayoutVariant();
  const { state, currentQuestion, startGame, playAgain } = useTriviaRushDemo(layout.type === 'desktop');

  // Derive reveal states from lastResult
  const getRevealState = (side: 'A' | 'B') => {
    if (!state.lastResult) return null;
    return side === state.lastResult.correctAnswer ? 'correct' as const : 'wrong' as const;
  };

  const getRevealValue = (side: 'A' | 'B') => {
    if (!state.lastResult) return null;
    return side === 'A' ? state.lastResult.formattedValueA : state.lastResult.formattedValueB;
  };

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Trivia Rush Demo</title>
      </Helmet>

      <RushTopBar
        isLeaderboardOpen={false}
        onLeaderboardToggle={() => {}}
      />
      <div
        className="min-h-screen flex flex-col relative animate-fade-up opacity-0"
        style={{ backgroundColor: 'var(--rush-bg-primary)' }}
      >
        <RushWavyGrid />
        <div className="w-full max-w-[480px] sm:max-w-[560px] md:max-w-[640px] lg:max-w-[720px] mx-auto px-4 pt-14 pb-6 flex-1 flex flex-col relative z-[1]">
          {/* Idle / Loading → Start Screen */}
          {(state.status === 'idle' || state.status === 'loading') && (
            <RushStartScreen
              onStart={startGame}
              isLoading={state.status === 'loading'}
              error={state.error}
            />
          )}

          {/* Playing / Answered → Game UI */}
          {(state.status === 'playing' || state.status === 'answered') && currentQuestion && (
            <div className="flex-1 flex flex-col">
              {/* Score bar */}
              <div className="flex items-center justify-between mb-4">
                <span
                  className="text-sm font-medium"
                  style={{ color: 'var(--rush-text-muted)' }}
                >
                  Score
                </span>
                <span
                  className="text-2xl font-bold font-mono"
                  style={{ color: 'var(--rush-purple-accent)' }}
                >
                  {state.score}
                </span>
              </div>

              {/* Timer */}
              <RushTimer
                timeRemaining={state.timeRemaining}
                timerDuration={state.timerDuration}
              />

              {/* Question text */}
              <div className="mt-8 mb-6 text-center">
                <p
                  className="text-base sm:text-lg"
                  style={{ color: 'var(--rush-text-muted)' }}
                >
                  Which company has the higher
                </p>
                <p
                  className="text-xl sm:text-2xl font-bold mt-1"
                  style={{ color: 'var(--rush-text-primary)', fontFamily: 'DM Serif Display, serif' }}
                >
                  {currentQuestion.metricDisplayName}?
                </p>
              </div>

              {/* Company cards side-by-side with VS divider */}
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3 md:gap-4">
                <QuestionCard
                  company={currentQuestion.stockA}
                  label="A"
                  onSelect={() => {}}
                  disabled={state.status === 'answered'}
                  revealState={getRevealState('A')}
                  revealValue={getRevealValue('A')}
                />

                <span
                  className="text-sm sm:text-base md:text-lg font-bold"
                  style={{ color: 'var(--rush-text-muted)' }}
                >
                  VS
                </span>

                <QuestionCard
                  company={currentQuestion.stockB}
                  label="B"
                  onSelect={() => {}}
                  disabled={state.status === 'answered'}
                  revealState={getRevealState('B')}
                  revealValue={getRevealValue('B')}
                />
              </div>

              {/* Streak indicator */}
              {state.score > 0 && (
                <div className="mt-6 text-center">
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--rush-correct)' }}
                  >
                    {state.score} correct streak
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Game Over */}
          {state.status === 'game-over' && (
            <RushGameOver score={state.score} onPlayAgain={playAgain} error={state.error} lastMetricName={currentQuestion?.metricDisplayName} />
          )}
        </div>

        <p className="text-xs text-center px-6 py-4 max-w-xl mx-auto relative z-[1]" style={{ color: 'var(--rush-text-muted)', opacity: 0.5 }}>
          Wallstreetle is for entertainment and educational purposes only and does not constitute financial advice. Always consult a licensed financial advisor for investment guidance specific to your personal situation. Data accuracy is not guaranteed and cannot be relied on to make investment decisions. Always conduct your own research before making investment decisions.
        </p>
      </div>
    </>
  );
};
